import React, { forwardRef, useId } from 'react';
import cn from 'classnames';
import { UseFormRegisterReturn } from 'react-hook-form';
import { useThemeContext } from '../../../utils/providers/theme-context';

type CheckboxProps = {
  id?: string;
  name?: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  defaultChecked?: boolean;
  checked?: boolean;
  disabled?: boolean;
  value?: string | number | readonly string[];
  register?: UseFormRegisterReturn;
  registerOptions?: object;
  className?: string;
  labelClass?: string;
  descriptionClass?: string;
  wrapperClass?: string;
  variant?: 'default' | 'primary';
  'aria-label'?: string;
};

type CSSWithVars = React.CSSProperties & {
  [key: `--${string}`]: string | number | undefined;
};

type ColorToken =
  | 'activeText'
  | 'normalText'
  | 'secondLayer'
  | 'border'
  | 'borderPrimary'
  | 'inputBorder'
  | 'icon'
  | 'inactiveText'
  | 'dropdownText'
  | 'baseLayer'
  | 'firstLayer'
  | 'thirdLayer'
  | 'datagridBg';

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>((props, ref) => {
  const {
    id,
    name,
    label,
    description,
    error,
    onChange,
    onBlur,
    defaultChecked,
    checked,
    disabled,
    value,
    register,
    className,
    labelClass,
    descriptionClass,
    wrapperClass,
    ...rest
  } = props;

  const themeCtx = useThemeContext();
  const theme = themeCtx?.theme ?? 'light';
  const getColour = themeCtx?.getColour;

  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const safeColour = (token: ColorToken): string =>
    getColour?.(token) ?? 'inherit';

  // Base palette (shared with inputs)
  // getColour('activeText') already returns the safe (visibility-adjusted) brand color
  const active = safeColour('activeText');
  const text = safeColour('normalText');
  const surface = safeColour('secondLayer');
  const borderBase = safeColour('border');
  const borderHover = safeColour('border');
  const desc = safeColour('inactiveText');
  const disabledBg = pick('rgba(17,24,39,0.03)', 'rgba(255,255,255,0.06)');

  // Checkbox specifics
  // control surface: use theme colors properly
  const cbBg = surface;
  // checked color: use safe version from ThemeContext (already visibility-adjusted)
  const cbAccent = active;

  // Expose variables once on the wrapper
  const styleVars: CSSWithVars = {
    // Shared tokens
    '--in-border': borderBase,
    '--in-border-hover': borderHover,
    '--in-ring': active,
    '--in-disabled-bg': disabledBg,
    // Checkbox-specific tokens
    '--cb-bg': cbBg,
    '--cb-accent': cbAccent,
    '--cb-label': text,
    '--cb-desc': desc
  };

  const checkboxClass = cn(
    'col-start-1 row-start-1 appearance-none rounded-[3px]',
    // Only apply default size if className doesn't contain size override
    !className?.includes('size-') && 'size-4',
    // base/border (no hover effects)
    'bg-[var(--cb-bg)] border border-[var(--in-border)]',
    // checked / indeterminate fill + border
    'checked:bg-[var(--cb-accent)] checked:border-[var(--cb-accent)]',
    'indeterminate:bg-[var(--cb-accent)] indeterminate:border-[var(--cb-accent)]',
    // focus ring
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--in-ring)]',
    // disabled
    'disabled:bg-[var(--in-disabled-bg)] disabled:border-[var(--in-border)] disabled:opacity-50',
    // a11y forced-colors fallback
    'forced-colors:appearance-auto',
    className
  );

  const descClasses = cn(
    'text-xs mt-0.5',
    'text-[var(--cb-desc)]',
    descriptionClass
  );

  const autoId = useId();
  const baseId = id || register?.name || autoId;
  const descId = description ? `${baseId}-desc` : undefined;
  const errId = error ? `${baseId}-err` : undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ariaLabel = (rest as any)['aria-label'] as string | undefined;
  const ariaDescribedBy =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [descId, errId, (rest as any)['aria-describedby']]
      .filter(Boolean)
      .join(' ') || undefined;

  const userSetTextColor = !!labelClass && /\btext-/.test(labelClass);
  const labelTitle = typeof label === 'string' ? label : undefined;

  return (
    <div className={cn('relative group', wrapperClass)} style={styleVars}>
      <label
        htmlFor={baseId}
        className={cn(
          'inline-flex items-center gap-3 cursor-pointer select-none py-1'
        )}
      >
        <span
          className={cn(
            'flex shrink-0 items-center',
            // Only apply default spacing if wrapperClass doesn't override
            !wrapperClass?.includes('!p-0') && 'h-6 pt-0.5'
          )}
        >
          <span
            className={cn(
              'group relative grid place-items-center',
              // Match the checkbox size dynamically
              !className?.includes('size-') && 'size-4'
            )}
          >
            <input
              id={baseId}
              name={name || register?.name}
              type="checkbox"
              ref={ref || (register?.ref as React.Ref<HTMLInputElement>)}
              onChange={onChange || register?.onChange}
              onBlur={onBlur || register?.onBlur}
              checked={checked}
              defaultChecked={defaultChecked}
              disabled={disabled}
              value={value}
              className={checkboxClass}
              aria-label={label == null ? ariaLabel : undefined}
              aria-describedby={ariaDescribedBy}
              aria-invalid={!!error || undefined}
            />

            <svg
              fill="none"
              viewBox="0 0 14 14"
              className={cn(
                'pointer-events-none absolute inset-0 m-auto block stroke-white group-has-disabled:stroke-black/25 dark:group-has-disabled:stroke-white/25',
                // Scale SVG checkmark based on checkbox size
                !className?.includes('size-') ? 'size-3' : 'size-2.5'
              )}
              aria-hidden="true"
            >
              <path
                d="M3 8L6 11L11 3.5"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="opacity-0 group-has-checked:opacity-100"
              />
              <path
                d="M3 7H11"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="opacity-0 group-has-indeterminate:opacity-100"
              />
            </svg>
          </span>
        </span>

        {(label || description) && (
          <span className="flex min-w-0 flex-col">
            {label != null && (
              <span
                className={cn(
                  'text-sm font-medium leading-5 pt-0.5 transition-colors duration-150',
                  labelClass
                )}
                style={
                  userSetTextColor ? undefined : { color: 'var(--cb-label)' }
                }
                title={labelTitle}
              >
                {label}
              </span>
            )}
            {description != null && (
              <span id={descId} className={descClasses}>
                {description}
              </span>
            )}
          </span>
        )}
      </label>

      {error && (
        <p id={errId} className="text-red-500 dark:text-red-400 text-xs mt-1">
          {error}
        </p>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';
export default Checkbox;
